export type CouponBalanceKind = 'fixed' | 'percent'
export type CouponBalanceMode = CouponBalanceKind | 'preserve'

const EURO_SYMBOL = '\u20ac'

const normalizeRawBalance = (value: unknown) =>
  String(value ?? '')
    .trim()
    .replace(/eur/gi, EURO_SYMBOL)
    .replace(/\s+/g, '')

const normalizeNumberForParsing = (value: unknown): string | undefined => {
  const cleaned = normalizeRawBalance(value)
    .replace(new RegExp(EURO_SYMBOL, 'g'), '')
    .replace(/%/g, '')
    .replace(/[^0-9,.\-+]/g, '')
    .replace(/(?!^)[+-]/g, '')

  if (!cleaned || !/[0-9]/.test(cleaned)) return undefined

  const lastComma = cleaned.lastIndexOf(',')
  const lastDot = cleaned.lastIndexOf('.')
  const decimalSeparator = lastComma > lastDot ? ',' : lastDot > lastComma ? '.' : ''

  if (!decimalSeparator) return cleaned

  const thousandsSeparator = decimalSeparator === ',' ? '.' : ','
  const escapedThousandsSeparator = thousandsSeparator === '.' ? '\\.' : thousandsSeparator
  const integerPart = cleaned
    .slice(0, cleaned.lastIndexOf(decimalSeparator))
    .replace(new RegExp(escapedThousandsSeparator, 'g'), '')
  const fractionPart = cleaned
    .slice(cleaned.lastIndexOf(decimalSeparator) + 1)
    .replace(/[,.]/g, '')

  return `${integerPart}.${fractionPart}`
}

export const getCouponBalanceKind = (balance: unknown): CouponBalanceKind =>
  normalizeRawBalance(balance).includes('%') ? 'percent' : 'fixed'

export const isCouponPercentBalance = (balance: unknown) =>
  getCouponBalanceKind(balance) === 'percent'

export const parseCouponBalanceValue = (balance: unknown): number | undefined => {
  const normalized = normalizeNumberForParsing(balance)
  if (!normalized) return undefined

  const value = Number(normalized)
  return Number.isFinite(value) ? value : undefined
}

export const parseCouponBalance = (balance: unknown):
  | { kind: CouponBalanceKind, value: number }
  | undefined => {
  const value = parseCouponBalanceValue(balance)
  if (typeof value !== 'number') return undefined

  return {
    kind: getCouponBalanceKind(balance),
    value,
  }
}

const formatCouponBalanceNumber = (value: number) => {
  const rounded = Number.isInteger(value)
    ? String(value)
    : value
      .toFixed(2)
      .replace(/0+$/, '')
      .replace(/\.$/, '')

  return rounded.replace('.', ',')
}

export const normalizeCouponBalance = (
  balance: unknown,
  mode: CouponBalanceMode = 'preserve',
) => {
  const value = parseCouponBalanceValue(balance)
  if (typeof value !== 'number') return ''

  const kind = mode === 'preserve'
    ? getCouponBalanceKind(balance)
    : mode

  return `${formatCouponBalanceNumber(value)}${kind === 'percent' ? '%' : EURO_SYMBOL}`
}
